import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Radar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { MonitorsTable } from '@/components/publications/importacoes/MonitorsTable'
import { AddProcessMonitorForm } from '@/components/publications/importacoes/AddProcessMonitorForm'
import { useAuth } from '@/contexts/AuthContext'
import { isSocio } from '@/lib/roles'
import { importacoesService, Monitor } from '@/services/importacoes-service'
import { apiErrorMessage } from '@/services/team-service'
import * as toast from '@/lib/toast'

/** Publicações › Monitorados: OABs (espelho de Equipe) e processos específicos. */
export function PublicationMonitorsPage() {
  const { user } = useAuth()
  const socio = isSocio(user?.role)
  const [monitors, setMonitors] = useState<Monitor[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setMonitors(await importacoesService.listMonitors())
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível carregar os monitoramentos'))
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggle = async (monitor: Monitor) => {
    setBusyId(monitor.id)
    try {
      await importacoesService.setMonitorStatus(monitor.id, monitor.status === 'ATIVO' ? 'PAUSADO' : 'ATIVO')
      toast.success(monitor.status === 'ATIVO' ? 'Monitoramento pausado' : 'Monitoramento retomado')
      await load()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  const canToggle = (monitor: Monitor) => socio || (monitor.tipo === 'PROCESSO' && monitor.createdBy === user?.id)

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Radar className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Monitorados</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><Link to="/publications">Caixa de entrada</Link></Button>
          <Button variant="outline" asChild><Link to="/publications/importacoes">Importações</Link></Button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <Card>
        <CardHeader>
          <CardTitle>Advogados (OAB)</CardTitle>
          <CardDescription>
            Vêm de Configurações › Equipe. Pausar aqui desliga o monitoramento da OAB (só sócio).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MonitorsTable
            monitors={monitors.filter((m) => m.tipo === 'OAB')}
            canToggle={canToggle}
            busyId={busyId}
            onToggle={toggle}
            emptyText="Nenhuma OAB monitorada. Cadastre em Configurações › Equipe."
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Processos</CardTitle>
          <CardDescription>
            Processos consultados pelo número, além das OABs. Quem cadastra passa a ver as publicações deles em “Eu”.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <AddProcessMonitorForm onAdded={load} />
          <MonitorsTable
            monitors={monitors.filter((m) => m.tipo === 'PROCESSO')}
            canToggle={canToggle}
            busyId={busyId}
            onToggle={toggle}
            emptyText="Nenhum processo monitorado."
          />
        </CardContent>
      </Card>
    </div>
  )
}
